/**
 * StemPackPage — permalink for a single marketplace pack
 * (`/marketplace/pack/:packId`). Anyone can preview each stem, remix the
 * pack straight into the studio, download the zip, or copy the page link.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ElpmLogo from "@/components/ElpmLogo";
import { toast } from "sonner";
import {
  ArrowLeft, Music2, Wand2, Download, Heart, Share2, Trash2, Play, Pause, Loader2, Link2,
} from "lucide-react";
import { getStemPack, toggleStemPackLike, deleteStemPack, downloadStemPack } from "@/lib/marketplaceApi";
import previewPlayer from "@/lib/marketplacePreviewPlayer";
import useAuth from "@/lib/useAuth";
import PackCover from "@/components/marketplace/PackCover";

const stemKey = (packId, name) => `${packId}::${name}`;

function Chip({ tone, children, testId }) {
  const tones = {
    emerald: "bg-emerald-500/10 text-emerald-200 ring-emerald-400/30",
    fuchsia: "bg-fuchsia-500/10 text-fuchsia-200 ring-fuchsia-400/30",
    cyan: "bg-cyan-500/10 text-cyan-200 ring-cyan-400/30",
    amber: "bg-amber-500/10 text-amber-200 ring-amber-400/30 uppercase tracking-wide",
    slate: "bg-white/5 text-slate-300 ring-white/10",
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ring-1 ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function StemRow({ pack, stem, index, snap }) {
  const key = stemKey(pack.pack_id, stem.name);
  const mine = snap.packId === key;
  const status = mine ? snap.status : "idle";
  return (
    <li
      data-testid={`pack-stem-row-${index}`}
      data-status={status}
      className={`relative flex items-center gap-3 overflow-hidden rounded-xl border px-3 py-2.5 transition ${
        status === "playing" ? "border-emerald-400/40 bg-emerald-500/[0.07]" : "border-white/10 bg-slate-900/40 hover:border-white/20"
      }`}
    >
      <button
        type="button"
        data-testid={`pack-stem-play-${index}`}
        onClick={() => previewPlayer.toggle(key, stem.name, stem.url)}
        aria-label={status === "playing" ? `Pause ${stem.name}` : `Play ${stem.name}`}
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ring-1 transition ${
          status === "playing"
            ? "bg-emerald-400 text-slate-950 ring-emerald-300"
            : status === "error"
            ? "bg-rose-500/20 text-rose-200 ring-rose-400/40"
            : "bg-white/5 text-emerald-200 ring-emerald-400/30 hover:bg-emerald-500/20"
        }`}
      >
        {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : status === "playing" ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 translate-x-px fill-current" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold capitalize text-white">{stem.name}</p>
        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
          {status === "playing" ? "Previewing · 30 s" : status === "error" ? "Preview unavailable — tap to retry" : "Tap to preview"}
        </p>
      </div>
      <a
        data-testid={`pack-stem-download-${index}`}
        href={stem.url}
        target="_blank"
        rel="noreferrer"
        className="rounded-lg p-2 text-slate-500 transition hover:bg-white/5 hover:text-white"
        title={`Open ${stem.name} stem`}
      >
        <Download className="h-4 w-4" />
      </a>
      {mine && status !== "idle" && (
        <div className="absolute inset-x-0 bottom-0 h-0.5 bg-white/5">
          <div className="h-full bg-gradient-to-r from-emerald-400 via-cyan-300 to-emerald-400 transition-[width] duration-150" style={{ width: `${Math.max(2, snap.progress * 100)}%` }} />
        </div>
      )}
    </li>
  );
}

export default function StemPackPage() {
  const { packId } = useParams();
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const [pack, setPack] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [snap, setSnap] = useState(() => previewPlayer.getState());

  useEffect(() => previewPlayer.subscribe(setSnap), []);
  useEffect(() => () => previewPlayer.stop(), []);

  useEffect(() => {
    let alive = true;
    setPack(null); setError(null);
    getStemPack(packId)
      .then((p) => { if (alive) setPack(p); })
      .catch((e) => { if (alive) setError(e?.message || "Pack not found"); });
    return () => { alive = false; };
  }, [packId]);

  useEffect(() => {
    if (pack?.title) document.title = `${pack.title} · ELPM Stems`;
    return () => { document.title = "ELPM Studio Pro"; };
  }, [pack?.title]);

  const copy = async (url, title, description) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(title, { description });
    } catch (e) {
      void e;
      toast.message("Copy this link", { description: url, duration: 12000 });
    }
  };

  const handleLike = async () => {
    if (!user) { toast.message("Sign in to like packs"); signIn?.(); return; }
    setBusy("like");
    try { setPack(await toggleStemPackLike(pack.pack_id)); }
    catch (e) { toast.error("Could not toggle like", { description: e.message }); }
    finally { setBusy(null); }
  };

  const handleDownload = async () => {
    setBusy("download");
    const id = toast.loading(`Packing ${pack.stem_count} stems…`);
    try {
      const { count } = await downloadStemPack(pack.pack_id, pack.title);
      toast.dismiss(id);
      toast.success(`Downloaded ${count}-stem pack`);
      setPack((p) => ({ ...p, download_count: (p.download_count || 0) + 1 }));
    } catch (e) { toast.dismiss(id); toast.error("Could not download pack", { description: e.message }); }
    finally { setBusy(null); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${pack.title}"? This cannot be undone.`)) return;
    setBusy("delete");
    try { await deleteStemPack(pack.pack_id); toast.success("Pack deleted"); navigate("/marketplace"); }
    catch (e) { toast.error("Could not delete pack", { description: e.message }); setBusy(null); }
  };

  const pageUrl = `${window.location.origin}/marketplace/pack/${encodeURIComponent(packId)}`;
  const remixUrl = `${window.location.origin}/studio?remix=${encodeURIComponent(packId)}`;
  const tempo = pack?.tempo_bpm != null ? `${Math.round(pack.tempo_bpm)} BPM` : null;
  const keyLabel = pack?.key || (pack?.key_root ? `${pack.key_root} ${pack.key_quality || ""}`.trim() : null);

  return (
    <div data-testid="stem-pack-page" className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-violet-950 text-white">
      <header className="sticky top-0 z-30 border-b border-white/5 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <button type="button" data-testid="pack-page-back-btn" onClick={() => navigate("/marketplace")} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-white/5 hover:text-white" title="Back to marketplace">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <ElpmLogo className="h-8" testId="stem-pack-logo" />
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-slate-300">Stems Marketplace</span>
          </div>
          <button type="button" data-testid="pack-page-copy-link-btn" onClick={() => copy(pageUrl, "Pack link copied", "Anyone can preview, remix or download from this page.")} className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:border-cyan-400/40 hover:text-cyan-200">
            <Link2 className="h-3.5 w-3.5" /> Copy link
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        {error ? (
          <div data-testid="pack-page-error" className="rounded-2xl border border-dashed border-white/10 bg-slate-900/30 py-20 text-center">
            <Music2 className="mx-auto mb-3 h-9 w-9 text-slate-700" />
            <p className="text-base font-medium text-slate-300">This pack isn&apos;t available</p>
            <p className="mt-1 text-sm text-slate-500">{error}</p>
            <button type="button" data-testid="pack-page-browse-btn" onClick={() => navigate("/marketplace")} className="mt-5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 px-5 py-2.5 text-sm font-semibold text-white hover:brightness-110">Browse the marketplace</button>
          </div>
        ) : !pack ? (
          <div data-testid="pack-page-loading" className="flex items-center justify-center gap-2 py-20 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading pack…</div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr]">
            <section>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Stem pack</p>
              <h1 data-testid="pack-page-title" className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
                <span className="bg-gradient-to-r from-emerald-300 via-cyan-300 to-violet-300 bg-clip-text text-transparent">{pack.title}</span>
              </h1>
              <p className="mt-3 text-sm text-slate-400">
                by <span data-testid="pack-page-publisher" className="text-slate-200">{pack.user_name || "Anonymous"}</span>
                <span className="mx-1.5 text-slate-600">·</span>{new Date(pack.created_at).toLocaleDateString()}
              </p>
              {pack.description && <p data-testid="pack-page-description" className="mt-4 max-w-xl text-base text-slate-300/90">{pack.description}</p>}
              <div className="mt-5 flex flex-wrap gap-2">
                <Chip tone="emerald" testId="pack-page-stem-count"><Music2 className="h-3 w-3" />{pack.stem_count} stem{pack.stem_count === 1 ? "" : "s"}</Chip>
                {tempo && <Chip tone="fuchsia">{tempo}</Chip>}
                {keyLabel && <Chip tone="cyan">Key · {keyLabel}</Chip>}
                {pack.genre && pack.genre !== "other" && <Chip tone="amber">{pack.genre}</Chip>}
                {pack.stems_model && <Chip tone="slate">{pack.stems_model}</Chip>}
              </div>
              {pack.tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {pack.tags.map((t) => <span key={t} className="rounded-md bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">#{t}</span>)}
                </div>
              )}
              <div className="mt-6 flex items-center gap-4 text-sm text-slate-400">
                <button type="button" data-testid="pack-page-like-btn" onClick={handleLike} disabled={busy === "like"} className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 transition disabled:opacity-50 ${pack.is_liked ? "bg-fuchsia-500/20 text-fuchsia-200 ring-1 ring-fuchsia-400/40" : "hover:bg-fuchsia-500/10 hover:text-fuchsia-200"}`}>
                  <Heart className={`h-4 w-4 ${pack.is_liked ? "fill-current" : ""}`} /><span data-testid="pack-page-like-count">{pack.like_count || 0}</span>
                </button>
                <span><b className="text-slate-200">{pack.remix_count || 0}</b> remix{(pack.remix_count || 0) === 1 ? "" : "es"}</span>
                <span><b className="text-slate-200">{pack.download_count || 0}</b> downloads</span>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                <button type="button" data-testid="pack-page-remix-btn" onClick={() => { toast.message("Loading stems into your studio…"); navigate(`/studio?remix=${encodeURIComponent(pack.pack_id)}`); }} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-110">
                  <Wand2 className="h-4 w-4" /> Remix in Studio
                </button>
                <button type="button" data-testid="pack-page-download-btn" onClick={handleDownload} disabled={busy === "download"} className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-5 py-2.5 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-500/20 disabled:opacity-50">
                  <Download className="h-4 w-4" /> {busy === "download" ? "Packing…" : "Download zip"}
                </button>
                <button type="button" data-testid="pack-page-share-remix-btn" onClick={() => copy(remixUrl, "Remix link copied", "Opens the studio with these stems pre-loaded.")} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm text-slate-300 transition hover:border-cyan-400/40 hover:text-cyan-200">
                  <Share2 className="h-4 w-4" /> Remix link
                </button>
                {pack.is_owner && (
                  <button type="button" data-testid="pack-page-delete-btn" onClick={handleDelete} disabled={busy === "delete"} className="inline-flex items-center gap-2 rounded-xl border border-rose-400/30 px-4 py-2.5 text-sm text-rose-200 transition hover:bg-rose-500/10 disabled:opacity-50">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                )}
              </div>
            </section>

            <section>
              <PackCover
                pack={pack}
                size="square"
                canGenerate={!!pack.is_owner}
                onUpdated={setPack}
                className="group mb-6 rounded-2xl ring-1 ring-white/10 shadow-2xl shadow-violet-950/40"
              />
              <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Stems · tap to preview</h2>
              <ul data-testid="pack-page-stems" className="space-y-2">
                {(pack.stems || []).map((s, i) => <StemRow key={`${s.name}-${i}`} pack={pack} stem={s} index={i} snap={snap} />)}
              </ul>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
